import type { OrderHandoverSalesHandover } from "../types";

/** 合同交接带 * 必填对应的表单字段键，用于单元格高亮。 */
export type SalesHandoverRequiredFieldKey =
  | "contractSignerMemberId"
  | "catchOrderUserId"
  | "serviceConsultantUserId"
  | "dispatcherUserId"
  | "contractPeopleCount"
  | "contractUnitPriceAmount"
  | "travelFeePayTimeText"
  | "contractTemplateType"
  | "localInvoiceAmountDesc"
  | "isAdvanceMoney"
  | "isCollectTransportFee"
  | "isCollectIntangibleOrSchoolEnterpriseFee"
  | "collectIntangibleAmount"
  | "collectSchoolEnterpriseAmount"
  | "teacherHouseRemark"
  | "teacherFoodRemark"
  | "teacherTicketRemark"
  | "teacherTransportRemark";

export type SalesHandoverRequiredError = {
  label: string;
  field: SalesHandoverRequiredFieldKey;
};

/** 定稿校验可用的表单草稿，优先于后端快照判断是否已填。 */
export type SalesHandoverRequiredDraft = Partial<{
  contractSignerMemberId: string;
  contractSignerNames: string;
  catchOrderUserId: string;
  serviceConsultantUserId: string;
  dispatcherUserId: string;
  contractPeopleCount: number | string;
  contractUnitPriceAmount: string;
  travelFeePayTimeText: string;
  contractTemplateType: string;
  localInvoiceAmountDesc: string;
  isAdvanceMoney: boolean | string | null;
  isCollectTransportFee: boolean | string | null;
  isCollectIntangibleOrSchoolEnterpriseFee: boolean | string | null;
  collectIntangibleAmount: string;
  collectSchoolEnterpriseAmount: string;
  teacherHouseRemark: string;
  teacherFoodRemark: string;
  teacherTicketRemark: string;
  teacherTransportRemark: string;
}>;

/** 判断文本类必填是否已填；占位文案视为未填。 */
function hasRequiredText(value: string | undefined | null): boolean {
  const text = value?.trim() ?? "";
  if (!text) {
    return false;
  }
  return !["待填写", "待选择", "未填写", "未维护", "待确认"].includes(text);
}

/** 判断人员类必填：有用户 ID 或有效姓名快照即可。 */
function hasRequiredStaff(userId: string | undefined, userName: string | undefined): boolean {
  if (userId?.trim()) {
    return true;
  }
  return hasRequiredText(userName);
}

/** 判断金额/数量类必填：允许 0，不允许空。 */
function hasRequiredAmount(value: string | number | undefined | null): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "number") {
    return !Number.isNaN(value);
  }
  return value.trim() !== "";
}

/** 草稿优先，否则回退销售快照。 */
function pickDraft<T>(draftValue: T | undefined, salesValue: T | undefined): T | undefined {
  return draftValue !== undefined ? draftValue : salesValue;
}

/** 布尔必填：草稿里的 "true"/"false" 也算已选。 */
function hasRequiredBool(value: boolean | string | null | undefined): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value === "true" || value === "false";
  }
  return true;
}

/**
 * 收集合同交接定稿时的带 * 必填缺失项。
 * 保存草稿不调用；单订房/单订车跳过团队专属代收与老师标准字段。
 * 传入 draft（当前表单/名单推导值）时，已填字段不再报缺、也不应标红。
 */
export function collectSalesHandoverRequiredErrors(input: {
  sales: OrderHandoverSalesHandover | undefined;
  draft?: SalesHandoverRequiredDraft;
  signType?: string;
}): SalesHandoverRequiredError[] {
  const sales = input.sales;
  const draft = input.draft ?? {};
  const signType = input.signType?.trim() || "团队";
  const isSingleOrder = signType === "单订房" || signType === "单订车";
  const errors: SalesHandoverRequiredError[] = [];

  const signerNames = pickDraft(draft.contractSignerNames, sales?.contractSignerNames);
  const signerMemberId = draft.contractSignerMemberId;
  if (!hasRequiredText(signerNames) && !signerMemberId?.trim()) {
    errors.push({ label: "合同签定人", field: "contractSignerMemberId" });
  }
  if (
    !hasRequiredStaff(
      pickDraft(draft.catchOrderUserId, sales?.catchOrderUserId),
      sales?.catchOrderUserName,
    )
  ) {
    errors.push({ label: "抓单人", field: "catchOrderUserId" });
  }
  if (
    !hasRequiredStaff(
      pickDraft(draft.serviceConsultantUserId, sales?.serviceConsultantUserId),
      sales?.serviceConsultantName,
    )
  ) {
    errors.push({ label: "签单后的服务顾问", field: "serviceConsultantUserId" });
  }
  if (
    !hasRequiredStaff(
      pickDraft(draft.dispatcherUserId, sales?.dispatcherUserId),
      sales?.dispatcherUserName,
    )
  ) {
    errors.push({ label: "执行组计调", field: "dispatcherUserId" });
  }
  const peopleCount = pickDraft(draft.contractPeopleCount, sales?.contractPeopleCount);
  if (!hasRequiredAmount(peopleCount) || Number(peopleCount) <= 0) {
    errors.push({ label: "合同人数", field: "contractPeopleCount" });
  }
  if (
    !hasRequiredAmount(
      pickDraft(draft.contractUnitPriceAmount, sales?.contractUnitPriceAmount),
    )
  ) {
    errors.push({ label: "合同单价", field: "contractUnitPriceAmount" });
  }
  if (!hasRequiredText(pickDraft(draft.travelFeePayTimeText, sales?.travelFeePayTimeText))) {
    errors.push({ label: "旅游费用支付时间", field: "travelFeePayTimeText" });
  }
  if (!hasRequiredText(pickDraft(draft.contractTemplateType, sales?.contractTemplateType))) {
    errors.push({ label: "合同版本", field: "contractTemplateType" });
  }
  if (
    !hasRequiredText(pickDraft(draft.localInvoiceAmountDesc, sales?.localInvoiceAmountDesc))
  ) {
    errors.push({ label: "发票金额", field: "localInvoiceAmountDesc" });
  }
  if (!hasRequiredBool(pickDraft(draft.isAdvanceMoney, sales?.isAdvanceMoney))) {
    errors.push({ label: "是否虚拟团", field: "isAdvanceMoney" });
  }

  if (!isSingleOrder) {
    if (!hasRequiredBool(pickDraft(draft.isCollectTransportFee, sales?.isCollectTransportFee))) {
      errors.push({ label: "是否代收代付大交通", field: "isCollectTransportFee" });
    }
    if (
      !hasRequiredBool(
        pickDraft(
          draft.isCollectIntangibleOrSchoolEnterpriseFee,
          sales?.isCollectIntangibleOrSchoolEnterpriseFee,
        ),
      )
    ) {
      errors.push({
        label: "是否代收代付非遗校企",
        field: "isCollectIntangibleOrSchoolEnterpriseFee",
      });
    }
    if (
      !hasRequiredAmount(pickDraft(draft.collectIntangibleAmount, sales?.collectIntangibleAmount))
    ) {
      errors.push({ label: "代收代付非遗金额", field: "collectIntangibleAmount" });
    }
    if (
      !hasRequiredAmount(
        pickDraft(draft.collectSchoolEnterpriseAmount, sales?.collectSchoolEnterpriseAmount),
      )
    ) {
      errors.push({ label: "代收代付校企金额", field: "collectSchoolEnterpriseAmount" });
    }
    if (!hasRequiredText(pickDraft(draft.teacherHouseRemark, sales?.teacherHouseRemark))) {
      errors.push({ label: "老师住宿标准", field: "teacherHouseRemark" });
    }
    if (!hasRequiredText(pickDraft(draft.teacherFoodRemark, sales?.teacherFoodRemark))) {
      errors.push({ label: "老师餐标", field: "teacherFoodRemark" });
    }
    if (!hasRequiredText(pickDraft(draft.teacherTicketRemark, sales?.teacherTicketRemark))) {
      errors.push({ label: "老师门票要求", field: "teacherTicketRemark" });
    }
    if (
      !hasRequiredText(pickDraft(draft.teacherTransportRemark, sales?.teacherTransportRemark))
    ) {
      errors.push({ label: "老师交通要求", field: "teacherTransportRemark" });
    }
  }

  return errors;
}
